import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';
import { landingLinkFor } from '../../library/constants';
import ResourcesIds from '../../resources/ResourcesIds';
import { UserRole } from '../../types';
import { loadingBoxStyles } from './protectedRoute.styles';

interface ProtectedRouteProps {
    children: React.ReactNode;
    /**
     * Omitted means any signed-in user, which is what every route did before roles
     * existed. This is presentation only -- it keeps a page that would only produce
     * errors out of the way, and the API enforces the actual rule on every call.
     */
    allowedRoles?: UserRole[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
    const { isAuthenticated, isLoading, role } = useAuth();
    const { t: translate } = useTranslation();
    const location = useLocation();

    if (isLoading) {
        return (
            <Box sx={loadingBoxStyles}>
                <CircularProgress size={60} />
                <Typography variant="h6" color="text.secondary">
                    {translate(ResourcesIds.LOADING_TEXT)}
                </Typography>
            </Box>
        );
    }

    if (!isAuthenticated) {
        // Redirect to login page with return url
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (allowedRoles && !allowedRoles.includes(role ?? 'CUSTOMER')) {
        // Signed in, just not for this page -- so no return url: coming back after
        // another sign-in would land here again. The destination has to follow the role
        // rather than always be the dashboard, which is itself now gated: sending a
        // clinic admin there would only bounce them again.
        return <Navigate to={landingLinkFor(role)} replace />;
    }

    return <>{children}</>;
};

export default ProtectedRoute;
